import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { fetchGoingOut, setGoingOut } from "./GoingOutService";
import { useAuth } from "./AuthContext";
import type { GoingOutState, GoingOutStatus } from "../data/types";

/**
 * Kvällsfrågan, delad av alla som visar den.
 *
 * Svaret går att ändra på tre ställen — kartan innan man svarat, menyn
 * bakom avataren och den egna profilen — och de visar samma sak i samma
 * flik. Hämtade var och en för sig skulle profilen kunna säga "Going out"
 * medan menyn fortfarande säger "Maybe", och den som just ändrat sig
 * skulle inte veta vilken av dem som gällde.
 *
 * Servern äger fortfarande allt av betydelse: vilken natt det är, när
 * svaret går ut och vilka vänner som får synas. Det här är ett delat
 * skåp för svaret den skickade, inte en andra sanning.
 */

type GoingOutCtx = {
  state: GoingOutState | null;
  isLoading: boolean;
  isBusy: boolean;

  answer: (status: GoingOutStatus) => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<GoingOutCtx | null>(null);

export function GoingOutProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const [state, setState] = useState<GoingOutState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      // Utloggad finns ingen kväll att fråga om, och inget att ligga kvar
      // och visas för nästa person som loggar in i samma flik.
      setState(null);
      return;
    }

    setIsLoading(true);

    try {
      setState(await fetchGoingOut());
    } catch (e) {
      console.error("going out fetch failed", e);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const answer = useCallback(
    async (status: GoingOutStatus) => {
      if (isBusy) return;

      setIsBusy(true);

      try {
        // PUT svarar med hela tillståndet, så ingen behöver läsa om.
        setState(await setGoingOut(status));
      } catch (e) {
        console.error("going out update failed", e);
      } finally {
        setIsBusy(false);
      }
    },
    [isBusy]
  );

  const value = useMemo(
    () => ({ state, isLoading, isBusy, answer, refresh }),
    [state, isLoading, isBusy, answer, refresh]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGoingOut(): GoingOutCtx {
  const ctx = useContext(Ctx);

  if (!ctx) {
    throw new Error("useGoingOut must be used inside a GoingOutProvider");
  }

  return ctx;
}
